import { computed } from '@ember/object';
import BaseActualFilterAmplitude from '../../implementation-common/base-actual-filter-amplitude';
import * as Complex from '../../../process-data/complex';

export default BaseActualFilterAmplitude.extend({
  c1: null,
  c2: null,
  c5: null,
  r1: null,
  r2: null,
  opAmp: null,
  complexAlphaPlus: Complex.createComplex(0, 0),
  complexBetaPlus: Complex.createComplex(0, 0),
  complexDenominator: computed('c1', 'c2', 'c5', 'r1', 'r2', 'sImaginary', 'sSquared', function () {
    var sImaginary = this.sImaginary;
    var sSquared = this.sSquared;

    var c1 = this.c1;
    var c2 = this.c2;
    var c5 = this.c5;
    var r1 = this.r1;
    var r2 = this.r2;

    var denominatorRealTerm = sSquared * (c1 * c2 * r1 * r2 + c2 * c5 * r1 * r2) + 1;
    var denominatorImaginaryTerm = sImaginary * (c1 * r1 + c2 * r1 + c2 * r2 + c5 * r1);
    var result = Complex.createComplex(denominatorRealTerm, denominatorImaginaryTerm);
    return result;
  }),
  complexZlookbackDenominator: computed('c1', 'c2', 'c5', 'r1', 'r2', 'sImaginary', 'sSquared', function () {
    var sImaginary = this.sImaginary;
    var sSquared = this.sSquared;

    var c1 = this.c1;
    var c2 = this.c2;
    var c5 = this.c5;
    var r1 = this.r1;
    var r2 = this.r2;

    var ZlookbackDenomnatorRealTerm = sSquared * (c1 * c2 * r1 + c1 * c5 * r1 + c2 * c5 * r2);
    var ZlookbackDenomnatorImaginaryTerm = sImaginary * (c2 + c5 + sSquared * r1 * r2 * c1 * c2 * c5);
    var result = Complex.createComplex(ZlookbackDenomnatorRealTerm, ZlookbackDenomnatorImaginaryTerm);
    return result;
  }),
  complexFFDenominator: computed('complexROut', 'complexZlookbackDenominator', 'complexDenominator', function () {
    var complexROut = this.complexROut;
    var complexZlookbackDenominator = this.complexZlookbackDenominator;
    var complexDenominator = this.complexDenominator;

    var result = complexROut.mult(complexZlookbackDenominator).plus(complexDenominator);
    return result;
  }),
  complexRParallel: computed('complexROut', 'complexFFDenominator', 'complexDenominator', function () {
    var complexROut = this.complexROut;
    var complexFFDenominator = this.complexFFDenominator;
    var complexDenominator = this.complexDenominator;

    var rParallelNumerator = complexDenominator.mult(complexROut);
    var rParallelDenominator = complexFFDenominator;
    var result = rParallelNumerator.divide(rParallelDenominator);
    return result;
  }),
  complexFF: computed(
    'c1',
    'c2',
    'c5',
    'complexFFDenominator',
    'opAmp.rOut',
    'r1',
    'r2',
    'sImaginary',
    'sSquared',
    function () {
      var complexFFDenominator = this.complexFFDenominator;
      var sImaginary = this.sImaginary;
      var sSquared = this.sSquared;
      var rOut = this.opAmp.rOut;
      var c1 = this.c1;
      var c2 = this.c2;
      var c5 = this.c5;
      var r1 = this.r1;
      var r2 = this.r2;

      var FFnumerator = Complex.createComplex(
        sSquared * rOut * r1 * c1 * (c2 + c5),
        sSquared * sImaginary * rOut * r1 * r2 * c1 * c2 * c5,
      );
      var result = FFnumerator.divide(complexFFDenominator);
      return result;
    },
  ),
  complexAlphaMinus: computed('r1', 'r2', 'c1', 'c2', 'complexDenominator', 'sSquared', function () {
    var sSquared = this.sSquared;
    var complexDenominator = this.complexDenominator;

    var r1 = this.r1;
    var r2 = this.r2;
    var c1 = this.c1;
    var c2 = this.c2;

    var result = Complex.createComplex(sSquared * r1 * r2 * c1 * c2, 0).divide(complexDenominator);
    return result;
  }),
  complexBetaMinus: computed('c1', 'c2', 'c5', 'r1', 'r2', 'sImaginary', 'complexDenominator', 'sSquared', function () {
    var sImaginary = this.sImaginary;
    var sSquared = this.sSquared;
    var complexDenominator = this.complexDenominator;

    var r1 = this.r1;
    var r2 = this.r2;
    var c1 = this.c1;
    var c2 = this.c2;
    var c5 = this.c5;

    var complexBetaminusNumerator = Complex.createComplex(
      1 + sSquared * c2 * c5 * r1 * r2,
      sImaginary * (c1 * r1 + c2 * r1 + c5 * r1),
    );
    var result = complexBetaminusNumerator.divide(complexDenominator);
    return result;
  }),
  inputImpedance: computed('c1', 'c2', 'c5', 'r1', 'r2', 'sImaginary', 'complexAmplitude', function () {
    var r1 = this.r1;
    var r2 = this.r2;
    var c1 = this.c1;
    var c2 = this.c2;
    var c5 = this.c5;
    var sImaginary = this.sImaginary;
    var complexAmplitude = this.complexAmplitude;

    var complexOne = Complex.createComplex(1, 0);
    var complexNegOne = Complex.createComplex(-1, 0);
    var complexC1s = Complex.createComplex(0, 0);
    complexC1s.imaginary = c1 * sImaginary;

    var y1 = Complex.createComplex(r2, -1 / (c2 * sImaginary));
    y1 = complexOne.divide(y1);
    y1.real = y1.imaginary + c5 * sImaginary;
    var y1r1 = y1;
    y1r1.real *= r1;
    y1r1.imaginary *= r1;

    var top = Complex.createComplex(1, c1 * r1 * sImaginary);
    top = y1r1.plus(top);

    var bottom = Complex.createComplex(r1, 0);
    bottom = complexAmplitude.mult(bottom);
    bottom = bottom.plus(complexNegOne.subs(y1r1));
    bottom = complexC1s.mult(bottom);

    var result = top.divide(bottom);
    return result;
  }),
  computeMagnitude(complexAlphaMinus, complexFF) {
    let complexAlphaPlus = this.complexAlphaPlus;
    let complexA = this.complexA;
    let amplitudeDenominator = this.amplitudeDenominator;
    let amplitudeNumerator = this._computeAmplitudeNumerator(complexAlphaPlus, complexAlphaMinus, complexA, complexFF);
    let complexAmplitude = this._computeComplexAmplitude(amplitudeNumerator, amplitudeDenominator);
    return complexAmplitude.magnitude();
  },
});
